import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import RichTextEditor from "@/components/RichTextEditor";
import * as adminApi from "@/api/admin";
import type { SaveStatus } from "./types";

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">
        {children}
      </span>
      <Separator className="flex-1" />
    </div>
  );
}

interface ContentViewProps {
  slug: string;
  label: string;
  onSaveStatusChange: (status: SaveStatus) => void;
  saveStatus: SaveStatus;
  onSave: (slug: string, title: string, content: string) => Promise<void>;
}

export function ContentView({ slug, label, saveStatus, onSaveStatusChange, onSave }: ContentViewProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [origTitle, setOrigTitle] = useState("");
  const [origContent, setOrigContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const settling = useRef(false);

  const isDirty = title !== origTitle || content !== origContent;

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    onSaveStatusChange("idle");
    settling.current = true;
    adminApi.getPageContent(slug)
      .then(({ title: t, content: c }) => {
        setTitle(t);
        setContent(c);
        setOrigTitle(t);
        setOrigContent(c);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  function handleContentChange(html: string) {
    setContent(html);
    if (settling.current) {
      setOrigContent(html);
      settling.current = false;
    }
  }

  async function handleSave() {
    await onSave(slug, title, content);
    setOrigTitle(title);
    setOrigContent(content);
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="px-4 md:px-8 py-6 space-y-6">
          {loadError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Failed to load page content. Please refresh and try again.</AlertDescription>
            </Alert>
          )}

          {saveStatus === "error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Failed to save changes. Please try again.</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <SectionHeading>Page Title</SectionHeading>
            <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 space-y-1">
              <label className="text-sm font-medium text-gray-700">Title</label>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full border border-gray-400 text-[9px] text-center leading-3">i</span>
                Shown as the main heading on the public page.
              </p>
              {loading ? (
                <Skeleton className="h-9 w-full mt-2" />
              ) : (
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`${label} title`}
                  className="mt-2"
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <SectionHeading>Content</SectionHeading>
            {loading ? (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex gap-1 px-3 py-2 border-b border-gray-200 bg-gray-50">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-7 w-7 rounded" />
                  ))}
                </div>
                <div className="px-4 py-3 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ) : (
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                minHeight="256px"
                disabled={loading}
              />
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 md:left-56 right-0 bg-white border-t border-gray-200 px-4 md:px-8 py-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {saveStatus === "saving" ? (
            "Saving…"
          ) : saveStatus === "saved" ? (
            <span className="text-green-600 flex items-center gap-1.5">
              <Check size={14} /> All changes saved
            </span>
          ) : isDirty ? (
            "You have unsaved changes"
          ) : (
            "No pending changes"
          )}
        </p>
        <Button
          onClick={handleSave}
          disabled={saveStatus === "saving" || loading || !isDirty}
          className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white px-6 disabled:opacity-50"
        >
          {saveStatus === "saving" ? (
            <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>
          ) : saveStatus === "saved" ? (
            <><Check size={14} className="mr-2" /> Saved</>
          ) : (
            "Save changes"
          )}
        </Button>
      </div>
    </>
  );
}
